import React, { useState } from 'react';
import { FiTrendingUp, FiActivity, FiCalendar, FiClock } from 'react-icons/fi';

const TimelineTab = ({ insightData, Section }) => {
  const [futureExpanded, setFutureExpanded] = useState(true);
  const onToggleFuture = React.useCallback(() => setFutureExpanded(v => !v), []);
  const [historicalExpanded, setHistoricalExpanded] = useState(false);
  const onToggleHistorical = React.useCallback(() => setHistoricalExpanded(v => !v), []);

  if (!insightData?.heptapod_dimensional_analysis?.temporal_intelligence) return null;

  const temporalIntel = insightData.heptapod_dimensional_analysis.temporal_intelligence;

  return (
    <>
      {/* Future Projections */}
      {temporalIntel.future_projections && (
        <Section title="Future Projections" iconComponent={FiTrendingUp} collapsible expanded={futureExpanded} onToggle={onToggleFuture}>
          {futureExpanded && (
            <div className="space-y-4">
              {/* Trajectory Models */}
              {temporalIntel.future_projections.trajectory_models && (
                <div className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 transition-all duration-200">
                  <div className="flex items-center gap-2 mb-4">
                    <FiActivity className="text-blue-600" />
                    <h4 className="text-sm font-semibold text-gray-700">Scenario Analysis</h4>
                  </div>
                  <div className="space-y-3">
                    {temporalIntel.future_projections.trajectory_models.map((scenario, idx) => (
                      <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <div className="font-semibold text-gray-700 text-sm">
                            {scenario.scenario}
                          </div>
                          <div className="flex gap-2">
                            <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-semibold">
                              {scenario.probability}% Probability
                            </span>
                            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-semibold">
                              {scenario.timeline}
                            </span>
                          </div>
                        </div>
                        {scenario.key_events && (
                          <div className="text-xs text-gray-600">
                            <strong>Key Events:</strong> {scenario.key_events.join(', ')}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Decision Timelines */}
              {temporalIntel.future_projections.decision_timelines && (
                <div className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 transition-all duration-200">
                  <div className="flex items-center gap-2 mb-4">
                    <FiCalendar className="text-green-600" />
                    <h4 className="text-sm font-semibold text-gray-700">Decision Timelines</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {temporalIntel.future_projections.decision_timelines.map((decision, idx) => (
                      <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                        <div className="text-xs text-gray-700 font-semibold mb-1">
                          {decision.decision_type?.toUpperCase()}
                        </div>
                        <div className="text-sm text-gray-800 font-bold mb-1">
                          {decision.estimated_date}
                        </div>
                        <div className="text-xs text-gray-600">
                          {decision.confidence}% confidence
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Intervention Impact */}
              {temporalIntel.future_projections.intervention_impact && (
                <div className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 transition-all duration-200">
                  <div className="flex items-center gap-2 mb-4">
                    <FiTrendingUp className="text-blue-600" />
                    <h4 className="text-sm font-semibold text-gray-700">Intervention Impact</h4>
                  </div>
                  <div className="space-y-3">
                    {temporalIntel.future_projections.intervention_impact.map((intervention, idx) => (
                      <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <div className="font-semibold text-gray-700 text-sm">
                            {intervention.intervention}
                          </div>
                          <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-semibold">
                            {intervention.success_probability}% Success
                          </span>
                        </div>
                        <div className="text-xs text-gray-600">
                          {intervention.expected_outcome}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Section>
      )}

      {/* Historical Context */}
      {temporalIntel.historical_context && (
        <Section title="Historical Context" iconComponent={FiClock} collapsible expanded={historicalExpanded} onToggle={onToggleHistorical}>
          {historicalExpanded && (
            <div className="space-y-4">
              {/* Key Patterns */}
              {temporalIntel.historical_context.key_patterns && (
                <div className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 transition-all duration-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-4">Key Patterns</h4>
                  <div className="space-y-3">
                    {temporalIntel.historical_context.key_patterns.map((pattern, idx) => (
                      <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <div className="font-semibold text-gray-700 text-sm">
                            {pattern.pattern_type}
                          </div>
                          <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-semibold">
                            {pattern.frequency}
                          </span>
                        </div>
                        <div className="text-xs text-gray-600 mb-2">
                          {pattern.description}
                        </div>
                        <div className="text-xs text-gray-500">
                          <strong>Impact:</strong> {pattern.impact_on_current}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Relationship Evolution */}
              {temporalIntel.historical_context.relationship_evolution && (
                <div className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 transition-all duration-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-4">Relationship Evolution</h4>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-700 leading-relaxed">
                    {temporalIntel.historical_context.relationship_evolution}
                  </div>
                </div>
              )}

              {/* Milestone Correlations */}
              {temporalIntel.historical_context.milestone_correlations && (
                <div className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 transition-all duration-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-4">Milestone Correlations</h4>
                  <div className="space-y-3">
                    {temporalIntel.historical_context.milestone_correlations.map((correlation, idx) => (
                      <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <div className="text-xs text-gray-500">
                            {correlation.past_event}
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-semibold capitalize ${
                            correlation.correlation_strength === 'strong' ? 'bg-green-100 text-green-800' : 
                            correlation.correlation_strength === 'moderate' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {correlation.correlation_strength}
                          </span>
                        </div>
                        <div className="text-xs text-gray-600">
                          <strong>Current Impact:</strong> {correlation.current_impact}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Section>
      )}
    </>
  );
};

export default TimelineTab;
